<template>
    <div
        class="group-icon-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="group-icon-modal-title"
    >
        <div class="group-icon-modal__dialog">
            <header class="group-icon-modal__header">
                <h3
                    id="group-icon-modal-title"
                    class="group-icon-modal__title"
                >
                    Update Group Icon
                </h3>

                <button
                    type="button"
                    class="group-icon-modal__close"
                    aria-label="Close update group icon popup"
                    :disabled="isSubmitting"
                    @click="closeModal"
                >
                    <i
                        class="fa fa-times"
                        aria-hidden="true"
                    ></i>
                </button>
            </header>

            <div class="group-icon-modal__body">
                <div
                    v-if="isLoading"
                    class="group-icon-modal__status"
                    role="status"
                >
                    <i
                        class="fa fa-spinner fa-spin"
                        aria-hidden="true"
                    ></i>

                    <span>Loading group icon...</span>
                </div>

                <div
                    v-else-if="loadError"
                    class="
                        group-icon-modal__status
                        group-icon-modal__status--error
                    "
                    role="alert"
                >
                    <i
                        class="fa fa-exclamation-circle"
                        aria-hidden="true"
                    ></i>

                    <span>{{ loadError }}</span>

                    <button
                        type="button"
                        class="group-icon-modal__retry"
                        @click="loadGroupInfo"
                    >
                        Retry
                    </button>
                </div>

                <div
                    v-else-if="!imageSource"
                    class="group-icon-form"
                >
                    <div class="group-icon-form__preview">
                        <img
                            v-if="currentGroupImage"
                            :src="currentGroupImage"
                            class="group-icon-form__preview-image"
                            alt="Current group icon"
                        >

                        <div
                            v-else
                            class="group-icon-form__empty-preview"
                        >
                            <i
                                class="fa fa-users"
                                aria-hidden="true"
                            ></i>

                            <span>No group icon available</span>
                        </div>
                    </div>

                    <div class="group-icon-form__actions">
                        <label
                            class="
                                group-icon-form__button
                                group-icon-form__button--upload
                            "
                            :class="{
                                'group-icon-form__button--disabled':
                                    isSubmitting
                            }"
                            for="group-icon-file-input"
                        >
                            <input
                                id="group-icon-file-input"
                                ref="fileInput"
                                class="group-icon-form__file-input"
                                type="file"
                                accept="image/jpeg,image/png"
                                :disabled="isSubmitting"
                                @change="handleFileUpload"
                            >

                            <img
                                src="@/assets/images/camera2.png"
                                class="group-icon-form__button-icon"
                                alt=""
                                aria-hidden="true"
                            >

                            <span>Upload Photo</span>
                        </label>
                    </div>

                    <p
                        v-if="validationError"
                        class="group-icon-form__error"
                        role="alert"
                    >
                        {{ validationError }}
                    </p>

                    <p class="group-icon-form__hint">
                        Accepted formats: JPEG and PNG. Maximum size:
                        {{ maxFileSizeInMb }} MB.
                    </p>
                </div>

                <div
                    v-else
                    class="
                        group-icon-form
                        group-icon-form--cropping
                    "
                >
                    <div class="group-icon-form__cropper">
                        <vue-cropper
                            ref="cropper"
                            :src="imageSource"
                            :background="false"
                            :rotatable="true"
                            :scalable="true"
                            :zoomable="true"
                            :movable="true"
                            :crop-box-movable="true"
                            :crop-box-resizable="true"
                            :responsive="true"
                            :restore="false"
                            :view-mode="1"
                            :min-crop-box-width="150"
                            :min-crop-box-height="150"
                            :aspect-ratio="1"
                            alt="Selected group icon"
                        />
                    </div>

                    <p
                        v-if="validationError"
                        class="group-icon-form__error"
                        role="alert"
                    >
                        {{ validationError }}
                    </p>

                    <div class="group-icon-form__actions">
                        <button
                            type="button"
                            class="
                                group-icon-form__button
                                group-icon-form__button--cancel
                            "
                            :disabled="isSubmitting"
                            @click="cancelCrop"
                        >
                            <img
                                src="@/assets/images/remove.png"
                                class="group-icon-form__button-icon"
                                alt=""
                                aria-hidden="true"
                            >

                            <span>Cancel</span>
                        </button>

                        <button
                            type="button"
                            class="
                                group-icon-form__button
                                group-icon-form__button--save
                            "
                            :disabled="isSubmitting"
                            @click="saveCroppedImage"
                        >
                            <i
                                v-if="isSubmitting"
                                class="fa fa-spinner fa-spin"
                                aria-hidden="true"
                            ></i>

                            <img
                                v-else
                                src="@/assets/images/camera2.png"
                                class="group-icon-form__button-icon"
                                alt=""
                                aria-hidden="true"
                            >

                            <span>
                                {{
                                    isSubmitting
                                        ? 'Updating...'
                                        : 'Crop and Save'
                                }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import 'cropperjs/dist/cropper.css';

import VueCropper from 'vue-cropperjs';
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS = 'body_right_scroll_remove';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = [
    'image/jpeg',
    'image/png'
];

export default {
    name: 'UpdateGroupIconPopup',

    components: {
        VueCropper
    },

    props: {
        updateGroupIconID: {
            type: [String, Number],
            required: true
        }
    },

    data() {
        return {
            currentGroupImage: '',
            imageSource: '',
            selectedFileName: '',
            selectedFileType: '',
            isLoading: false,
            isSubmitting: false,
            loadError: '',
            validationError: ''
        };
    },

    computed: {
        groupId() {
            return this.updateGroupIconID || null;
        },

        maxFileSizeInMb() {
            return MAX_FILE_SIZE / (1024 * 1024);
        }
    },

    watch: {
        updateGroupIconID(newGroupId, oldGroupId) {
            if (
                newGroupId &&
                String(newGroupId) !== String(oldGroupId)
            ) {
                this.resetSelection();
                this.loadGroupInfo();
            }
        }
    },

    mounted() {
        this.addGlobalListeners();
        this.loadGroupInfo();
    },

    beforeDestroy() {
        this.removeGlobalListeners();
        this.releaseImageSource();
    },

    methods: {
        async loadGroupInfo(emitUpdatedData = false) {
            if (!this.groupId) {
                this.loadError =
                    'Group information is unavailable.';

                return;
            }

            this.isLoading = true;
            this.loadError = '';

            try {
                const response = await this.$store.dispatch(
                    'fetchGroupChatDetailsById',
                    {
                        id: this.groupId
                    }
                );

                const groupData =
                    this.extractResponseData(response);

                if (!groupData) {
                    throw new Error(
                        'Group information could not be loaded.'
                    );
                }

                this.currentGroupImage =
                    groupData.group_image || '';

                if (emitUpdatedData) {
                    bus.$emit(
                        'updateGroupInfoDataBus',
                        groupData
                    );
                }

                return groupData;
            } catch (error) {
                this.loadError = this.getErrorMessage(
                    error,
                    'Unable to load the group icon. Please try again.'
                );

                return null;
            } finally {
                this.isLoading = false;
            }
        },

        handleFileUpload(event) {
            const input = event.target;
            const file =
                input.files && input.files.length
                    ? input.files[0]
                    : null;

            this.validationError = '';

            if (!file) {
                return;
            }

            if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                this.validationError =
                    'Please select a JPEG or PNG image.';

                this.resetFileInput();

                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                this.validationError =
                    `The selected image must be smaller than ` +
                    `${this.maxFileSizeInMb} MB.`;

                this.resetFileInput();

                return;
            }

            if (typeof FileReader !== 'function') {
                this.validationError =
                    'Your browser does not support image uploads.';

                this.resetFileInput();

                return;
            }

            this.selectedFileName = file.name;
            this.selectedFileType = file.type;

            const reader = new FileReader();

            reader.onload = (readerEvent) => {
                const result =
                    readerEvent &&
                    readerEvent.target &&
                    readerEvent.target.result;

                if (!result) {
                    this.validationError =
                        'Unable to read the selected image.';

                    return;
                }

                this.imageSource = result;
            };

            reader.onerror = () => {
                this.validationError =
                    'Unable to read the selected image.';

                this.resetSelection();
            };

            reader.readAsDataURL(file);
        },

        async saveCroppedImage() {
            if (this.isSubmitting || !this.imageSource) {
                return;
            }

            const cropper = this.$refs.cropper;

            if (!cropper) {
                this.validationError =
                    'Image cropper is unavailable. Please try again.';

                return;
            }

            this.isSubmitting = true;
            this.validationError = '';

            try {
                const canvas = cropper.getCroppedCanvas({
                    width: 400,
                    height: 400,
                    imageSmoothingEnabled: true,
                    imageSmoothingQuality: 'high'
                });

                if (!canvas) {
                    throw new Error(
                        'Unable to crop the selected image.'
                    );
                }

                const outputType =
                    this.selectedFileType === 'image/png'
                        ? 'image/png'
                        : 'image/jpeg';

                const imageQuality =
                    outputType === 'image/jpeg'
                        ? 0.9
                        : undefined;

                const imageDataUrl = canvas.toDataURL(
                    outputType,
                    imageQuality
                );

                const base64Image =
                    this.removeDataUrlPrefix(imageDataUrl);

                if (!base64Image) {
                    throw new Error(
                        'Unable to process the cropped image.'
                    );
                }

                const response = await this.$store.dispatch(
                    'updateGroupChatDetails',
                    {
                        data: {
                            id: this.groupId,
                            data: {
                                group_image: base64Image,
                                filename:
                                    this.selectedFileName ||
                                    this.getDefaultFileName(
                                        outputType
                                    )
                            }
                        }
                    }
                );

                toast.success(
                    this.getSuccessMessage(
                        response,
                        'Group icon updated successfully.'
                    )
                );
                
                await Promise.all([
                    this.$store.dispatch('fetchPinnedChatRooms'),
                    this.$store.dispatch('fetchNormalChatRooms')
                ]);

                await this.loadGroupInfo(true);

                this.resetSelection();
            } catch (error) {
                this.validationError = this.getErrorMessage(
                    error,
                    'Unable to update the group icon. Please try again.'
                );

                toast.error(this.validationError);
            } finally {
                this.isSubmitting = false;
            }
        },

        cancelCrop() {
            if (this.isSubmitting) {
                return;
            }

            this.resetSelection();
        },

        resetSelection() {
            this.imageSource = '';
            this.selectedFileName = '';
            this.selectedFileType = '';
            this.validationError = '';

            this.resetFileInput();

            this.$nextTick(() => {
                const cropper = this.$refs.cropper;

                if (
                    cropper &&
                    typeof cropper.reset === 'function'
                ) {
                    cropper.reset();
                }
            });
        },

        resetFileInput() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        releaseImageSource() {
            if (
                this.imageSource &&
                this.imageSource.indexOf('blob:') === 0 &&
                window.URL &&
                typeof window.URL.revokeObjectURL ===
                    'function'
            ) {
                window.URL.revokeObjectURL(
                    this.imageSource
                );
            }
        },

        removeDataUrlPrefix(dataUrl) {
            if (!dataUrl || typeof dataUrl !== 'string') {
                return '';
            }

            return dataUrl.replace(
                /^data:image\/[a-zA-Z0-9.+-]+;base64,/,
                ''
            );
        },

        getDefaultFileName(imageType) {
            return imageType === 'image/png'
                ? 'group-icon.png'
                : 'group-icon.jpg';
        },

        extractResponseData(response) {
            if (
                response &&
                response.data &&
                response.data.data !== undefined
            ) {
                return response.data.data;
            }

            return null;
        },

        getSuccessMessage(response, fallbackMessage) {
            if (response && response.data) {
                return (
                    response.data.msg ||
                    response.data.message ||
                    fallbackMessage
                );
            }

            return fallbackMessage;
        },

        getErrorMessage(error, fallbackMessage) {
            if (
                error &&
                error.response &&
                error.response.data
            ) {
                return (
                    error.response.data.msg ||
                    error.response.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.data) {
                return (
                    error.data.msg ||
                    error.data.message ||
                    fallbackMessage
                );
            }

            if (error && error.message) {
                return error.message;
            }

            return fallbackMessage;
        },

        handleKeydown(event) {
            if (event.key === 'Escape') {
                if (this.imageSource) {
                    this.cancelCrop();
                    return;
                }

                this.closeModal();
            }
        },

        addGlobalListeners() {
            document.body.classList.add(
                BODY_SCROLL_CLASS
            );

            document.addEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        removeGlobalListeners() {
            document.body.classList.remove(
                BODY_SCROLL_CLASS
            );

            document.removeEventListener(
                'keydown',
                this.handleKeydown
            );
        },

        closeModal(forceClose = false) {
            if (this.isSubmitting && !forceClose) {
                return;
            }

            this.removeGlobalListeners();
            this.resetSelection();

            bus.$emit(
                'updateGroupIconPopupStateBus',
                false
            );
        }
    }
};
</script>

<style lang="scss" scoped>
$modal-overlay: rgba(0, 0, 0, 0.92);
$modal-background: #000000;
$modal-border: rgba(255, 255, 255, 0.1);
$text-primary: #ffffff;
$text-secondary: #b8b8b8;
$accent-color: #ffd500;
$primary-color: #87cefa;
$danger-color: #d94848;
$danger-hover: #ff5c5c;
$error-color: #ff6b6b;
$button-background: rgba(255, 255, 255, 0.12);

.group-icon-modal {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    overflow-y: auto;
    color: $text-primary;
    background-color: $modal-overlay;
    font-family: 'MyriadProRegular', 'Lato', sans-serif;

    &__dialog {
        width: 100%;
        max-width: 640px;
        margin: auto;
        overflow: hidden;
        border-radius: 5px;
        background-color: $modal-background;
        box-shadow: 0 0 13px rgba(255, 213, 0, 0.6);
    }

    &__header {
        position: relative;
        display: flex;
        align-items: center;
        min-height: 60px;
        padding: 15px 55px 15px 18px;
        border-bottom: 1px solid $modal-border;
    }

    &__title {
        margin: 0;
        color: $text-primary;
        font-size: 24px;
        font-weight: 600;
        line-height: 1.3;
    }

    &__close {
        position: absolute;
        top: 50%;
        right: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        padding: 0;
        border: 0;
        border-radius: 3px;
        color: $danger-color;
        background: transparent;
        cursor: pointer;
        transform: translateY(-50%);
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease,
            transform 0.2s ease;

        .fa {
            font-size: 20px;
        }

        &:hover:not(:disabled),
        &:focus-visible:not(:disabled) {
            color: $primary-color;
            background-color: rgba(255, 255, 255, 0.08);
            transform: translateY(-50%) scale(1.08);
        }

        &:focus-visible {
            outline: 2px solid $accent-color;
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }
    }

    &__body {
        padding: 20px;
    }

    &__status {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 220px;
        color: $text-secondary;
        font-size: 16px;
        text-align: center;

        .fa {
            margin-bottom: 10px;
            color: $accent-color;
            font-size: 25px;
        }

        &--error {
            color: $error-color;

            .fa {
                color: $error-color;
            }
        }
    }

    &__retry {
        min-width: 90px;
        margin-top: 15px;
        padding: 8px 15px;
        border: 0;
        border-radius: 3px;
        color: #22292f;
        background-color: $primary-color;
        font-family: inherit;
        font-size: 15px;
        cursor: pointer;

        &:hover,
        &:focus-visible {
            background-color: $accent-color;
        }

        &:focus-visible {
            outline: 2px solid $text-primary;
            outline-offset: 2px;
        }
    }
}

.group-icon-form {
    width: 100%;
    text-align: center;

    &--cropping {
        max-width: 430px;
        margin: 0 auto;
    }

    &__preview {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 300px;
        margin-bottom: 18px;
        overflow: hidden;
        border: 1px solid $modal-border;
        border-radius: 5px;
        background-color: rgba(255, 255, 255, 0.05);
    }

    &__preview-image {
        display: block;
        width: 100%;
        max-width: 100%;
        height: 300px;
        object-fit: contain;
        object-position: center;
    }

    &__empty-preview {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        min-height: 300px;
        color: $text-secondary;

        .fa {
            margin-bottom: 10px;
            color: rgba(255, 255, 255, 0.35);
            font-size: 55px;
        }
    }

    &__cropper {
        width: 100%;
        min-height: 340px;
        overflow: hidden;
        border: 1px solid $modal-border;
        border-radius: 5px;
        background-color: #111111;
    }

    &__actions {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        margin-top: 18px;
    }

    &__file-input {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
        white-space: nowrap;
    }

    &__button {
        position: relative;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 135px;
        min-height: 42px;
        margin: 0;
        padding: 9px 15px;
        border: 0;
        border-radius: 3px;
        color: $text-primary;
        background-color: $button-background;
        font-family: inherit;
        font-size: 17px;
        line-height: 1;
        cursor: pointer;
        transition:
            color 0.2s ease,
            background-color 0.2s ease,
            opacity 0.2s ease,
            transform 0.2s ease;

        .fa {
            margin-right: 7px;
        }

        &:hover:not(:disabled):not(
            .group-icon-form__button--disabled
        ),
        &:focus-visible:not(:disabled) {
            color: #22292f;
            background-color: $primary-color;
            transform: translateY(-1px);

            .group-icon-form__button-icon {
                filter: invert(1);
            }
        }

        &:focus-visible {
            outline: 2px solid $accent-color;
            outline-offset: 2px;
        }

        &:disabled,
        &--disabled {
            cursor: not-allowed;
            opacity: 0.55;
        }

        &--cancel {
            background-color: $danger-color;

            &:hover:not(:disabled),
            &:focus-visible:not(:disabled) {
                color: $text-primary;
                background-color: $danger-hover;
            }
        }

        &--save {
            background-color: $button-background;
        }
    }

    &__button-icon {
        display: inline-block;
        width: 22px;
        height: 22px;
        margin-right: 7px;
        object-fit: contain;
        vertical-align: middle;
        transition: filter 0.2s ease;
    }

    &__error {
        margin: 14px 0 0;
        color: $error-color;
        font-size: 14px;
        line-height: 1.4;
        text-align: center;
    }

    &__hint {
        margin: 10px 0 0;
        color: $text-secondary;
        font-size: 13px;
        line-height: 1.4;
        text-align: center;
    }
}

.group-icon-form__cropper /deep/ {
    .cropper-container {
        width: 100% !important;
        min-height: 340px;
    }

    .cropper-view-box,
    .cropper-face {
        border-radius: 2px;
    }

    .cropper-line,
    .cropper-point {
        background-color: $accent-color;
    }

    .cropper-view-box {
        outline-color: rgba(255, 213, 0, 0.8);
    }
}

@media (max-width: 600px) {
    .group-icon-modal {
        align-items: flex-start;
        padding: 10px;

        &__dialog {
            margin-top: auto;
            margin-bottom: auto;
        }

        &__header {
            min-height: 54px;
            padding: 13px 50px 13px 14px;
        }

        &__title {
            font-size: 20px;
        }

        &__body {
            padding: 14px;
        }
    }

    .group-icon-form {
        &__preview {
            min-height: 230px;
        }

        &__preview-image,
        &__empty-preview {
            height: 230px;
            min-height: 230px;
        }

        &__cropper {
            min-height: 280px;
        }

        &__actions {
            flex-direction: column-reverse;
        }

        &__button {
            width: 100%;
        }
    }

    .group-icon-form__cropper /deep/ {
        .cropper-container {
            min-height: 280px;
        }
    }
}
</style>